/**
 * Edit-lease state machine (decision R4, architecture §4). Pure: the LeaseClient feeds it
 * server responses and timer ticks.
 *
 *   idle ──request──▶ acquiring ──granted──▶ holding ──renew…──▶ holding
 *                       │  └──denied──▶ blocked (someone else edits; read-only + takeover)
 *                       └──network──▶ offline (local-first: keep editing if we were not blocked)
 *   holding ──denied (taken over)──▶ blocked      any ──release──▶ released
 *   any ──forbidden──▶ forbidden (no edit permission: permanently read-only)
 */
export type LeaseStatus =
  'idle' | 'acquiring' | 'holding' | 'blocked' | 'offline' | 'released' | 'forbidden';

export interface LeaseHolderInfo {
  kind: 'member' | 'guest';
  id: string;
  name: string;
}

export interface LeaseState {
  status: LeaseStatus;
  /** Current holder as last reported by the server (may be us). */
  holder: LeaseHolderInfo | null;
  /** Epoch ms when our lease expires (only while holding). */
  expiresAt: number | null;
  /** True when the most recent server answer granted us the lease. */
  mine: boolean;
  /** Whether we were displaced by someone else (used for the "taken over" notice). */
  takenOver: boolean;
}

export type LeaseEvent =
  | { type: 'request' }
  | {
      type: 'response';
      granted: boolean;
      holder: LeaseHolderInfo | null;
      expiresAt: string | null;
      ttlSeconds: number;
    }
  | { type: 'network-error' }
  | { type: 'forbidden' }
  | { type: 'released' }
  | { type: 'tick' };

export const initialLeaseState: LeaseState = {
  status: 'idle',
  holder: null,
  expiresAt: null,
  mine: false,
  takenOver: false,
};

export function leaseReducer(state: LeaseState, event: LeaseEvent, now: number): LeaseState {
  if (state.status === 'forbidden') return state;
  switch (event.type) {
    case 'request':
      if (state.status === 'holding' || state.status === 'blocked' || state.status === 'offline') {
        return state; // renewals keep the visible status stable
      }
      return { ...state, status: 'acquiring' };
    case 'response': {
      if (event.granted) {
        const expiresAt = event.expiresAt
          ? Date.parse(event.expiresAt)
          : now + event.ttlSeconds * 1000;
        return { status: 'holding', holder: event.holder, expiresAt, mine: true, takenOver: false };
      }
      return {
        status: 'blocked',
        holder: event.holder,
        expiresAt: null,
        mine: false,
        takenOver: state.status === 'holding' || state.takenOver,
      };
    }
    case 'network-error':
      return { ...state, status: 'offline' };
    case 'forbidden':
      return { ...initialLeaseState, status: 'forbidden' };
    case 'released':
      return { ...initialLeaseState, status: 'released' };
    case 'tick':
      if (state.status === 'holding' && state.expiresAt !== null && now >= state.expiresAt) {
        // Renewals failed silently long enough that the server dropped us.
        return { ...state, status: 'acquiring', mine: false, expiresAt: null };
      }
      return state;
  }
}

/**
 * May the local user write to the document? Holding the lease, or offline without a known
 * foreign editor (offline edits are merged later, decision R4).
 */
export function leaseAllowsEdit(state: LeaseState): boolean {
  if (state.status === 'holding') return true;
  if (state.status === 'offline') return state.mine || state.holder === null;
  return false;
}

/** Someone else edits: the UI shows the read-only banner with a takeover button. */
export function showTakeover(state: LeaseState): boolean {
  return state.status === 'blocked';
}
